import React, { useState, useEffect, useRef } from 'react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  ArrowLeft, CreditCard, Calendar, XCircle, Crown,
  CheckCircle, AlertCircle, Trash2, History, Home, User
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import subscriptionService from '../services/subscriptionService';
import stripeService from '../services/stripeService';
import GeometricBackground from '../components/ui/GeometricBackground';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';

const TEAL = '#006D77';

// 'expired' and 'inactive' immediately revoke access.
// 'cancelled'/'canceled' means the user cancelled but access runs until endDate, still shown on this page.
const IMMEDIATELY_REVOKED_STATUSES = ['expired', 'inactive'];

const isActiveSubscriptionRecord = (recordData, now = new Date()) => {
  const endDate = recordData?.endDate?.toDate?.();
  const status = String(recordData?.status || '').toLowerCase();
  const isImmediatelyRevoked = IMMEDIATELY_REVOKED_STATUSES.includes(status);
  return !!endDate && now < endDate && !isImmediatelyRevoked;
};

const SubscriptionManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // Store subscription status for free users too
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState(false);
  const [lastPaymentMethod, setLastPaymentMethod] = useState(null); // For "no active subscription" view
  const holdIntervalRef = useRef(null);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  // Load purchase history after subscription data is loaded
  useEffect(() => {
    if (!loading) {
      loadPurchaseHistory();
    }
  }, [subscriptionData, loading]);


  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!auth.currentUser) {
        navigate('/auth');
        return;
      }

      const userId = auth.currentUser.uid;

      // Get subscription status
      const status = await subscriptionService.getSubscriptionStatus(userId);
      
      // DEBUG: Log subscription status details
      console.log('🔍 Subscription Status Check:', {
        userId,
        status,
        isFree: status.isFree,
        isActive: status.isActive,
        subscriptionStatus: status.status,
        plan: status.plan,
        endDate: status.endDate,
        daysRemaining: status.daysRemaining
      });
      
      // Always store subscription status (even for free users)
      setSubscriptionStatus(status);
      
      // Check if user has active subscription
      if (status.isFree || !status.isActive) {
        // User doesn't have active subscription - but we still show their account status
        // Let's check if there's a subscription record that might not be synced to user document
        console.log('⚠️ User shows as free, checking subscriptions collection...');
        
        // Check subscriptions collection for any active subscriptions
        const subscriptionsRef = collection(db, 'subscriptions');
        const allSubsQuery = query(subscriptionsRef, where('userId', '==', userId));
        const allSubsSnapshot = await getDocs(allSubsQuery);
        
        if (!allSubsSnapshot.empty) {
          console.log('📋 Found subscription records:', allSubsSnapshot.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          })));
          
          // Check for active subscriptions that are not revoked/cancelled
          const activeSubs = allSubsSnapshot.docs.filter(doc => {
            const data = doc.data();
            const now = new Date();
            return isActiveSubscriptionRecord(data, now);
          });
          
          if (activeSubs.length > 0) {
            console.warn('⚠️ Found active subscription record while user document is free. Keeping read-only here to avoid unintended reactivation.', activeSubs[0].data());
          } else {
            // No active subscriptions found
            console.log('ℹ️ No active subscriptions found in subscriptions collection');
          }
        }
        
        // Also check user document directly
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('👤 User document data:', {
            subscriptionStatus: userData.subscriptionStatus,
            currentPlan: userData.currentPlan,
            subscriptionEndDate: userData.subscriptionEndDate?.toDate(),
            subscriptionStartDate: userData.subscriptionStartDate?.toDate()
          });
        }
        
        setSubscriptionData(null);
        setLoading(false);
        return;
      }

      // Get subscription details from Firestore - try subscriptions collection first
      // Check for both "active" and "completed" status subscriptions (completed might still be valid if not expired)
      const subscriptionsRef = collection(db, 'subscriptions');
      const allSubsQuery = query(subscriptionsRef, where('userId', '==', userId));
      const allSubsSnapshot = await getDocs(allSubsQuery);
      
      // Filter for subscriptions that haven't expired and are not revoked/cancelled
      const validSubscriptions = allSubsSnapshot.docs.filter(doc => {
        const data = doc.data();
        const now = new Date();
        return isActiveSubscriptionRecord(data, now);
      }).sort((a, b) => {
        // Sort by end date, most recent first
        const aDate = a.data().endDate?.toDate() || new Date(0);
        const bDate = b.data().endDate?.toDate() || new Date(0);
        return bDate - aDate;
      });
      
      const querySnapshot = { docs: validSubscriptions, empty: validSubscriptions.length === 0 };

      if (!querySnapshot.empty) {
        // Found subscription in subscriptions collection
        const subscriptionDoc = querySnapshot.docs[0];
        const subData = subscriptionDoc.data();
        
        // Get payment intent to fetch card details
        let paymentInfo = null;
        if (subData.paymentIntentId) {
          try {
            const paymentIntentRef = doc(db, 'paymentIntents', subData.paymentIntentId);
            const paymentIntentDoc = await getDoc(paymentIntentRef);
            if (paymentIntentDoc.exists()) {
              paymentInfo = paymentIntentDoc.data();
            }
          } catch (err) {
            console.error('Error fetching payment intent:', err);
          }
        }

        setSubscriptionData({
          ...subData,
          id: subscriptionDoc.id,
          startDate: subData.startDate?.toDate(),
          endDate: subData.endDate?.toDate(),
          createdAt: subData.createdAt?.toDate(),
        });
        // Don't overwrite payment method if we already have card details (from Stripe) - prevents flicker
        if (paymentInfo?.last4) {
          setPaymentMethod(paymentInfo);
        } else if (subData.paymentIntentId) {
          setPaymentMethod(prev => (prev?.last4 ? prev : paymentInfo));
          loadPaymentMethodFromStripe(subData.paymentIntentId);
        } else {
          setPaymentMethod(paymentInfo);
        }
      } else {
        // Fallback to user document data (for subscriptions created before subscriptions collection was used)
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Only set subscription data if user actually has an active subscription
          if (userData.subscriptionStatus === 'active' && userData.currentPlan && userData.subscriptionEndDate) {
            // Try to find payment intent from user's payment history
            let paymentInfo = null;
            try {
              const paymentIntentsRef = collection(db, 'paymentIntents');
              const paymentQuery = query(paymentIntentsRef, where('userId', '==', userId));
              const paymentSnapshot = await getDocs(paymentQuery);
              if (!paymentSnapshot.empty) {
                // Get the most recent successful payment
                const recentPayment = paymentSnapshot.docs
                  .filter(doc => doc.data().status === 'succeeded')
                  .sort((a, b) => {
                    const aTime = a.data().createdAt?.toDate() || new Date(0);
                    const bTime = b.data().createdAt?.toDate() || new Date(0);
                    return bTime - aTime;
                  })[0];
                if (recentPayment) {
                  paymentInfo = recentPayment.data();
                }
              }
            } catch (err) {
              console.error('Error fetching payment intent:', err);
            }
            
            setSubscriptionData({
              planId: userData.currentPlan,
              planName: userData.currentPlan === 'weekly' ? 'Weekly Plan' : userData.currentPlan === 'monthly' ? 'Monthly Plan' : userData.currentPlan === 'quarterly' ? '3 Months' : 'Premium',
              status: userData.subscriptionStatus,
              startDate: userData.subscriptionStartDate?.toDate(),
              endDate: userData.subscriptionEndDate?.toDate(),
              amount: paymentInfo?.amount,
              currency: paymentInfo?.currency || 'eur',
            });
            if (paymentInfo?.last4) {
              setPaymentMethod(paymentInfo);
            } else {
              setPaymentMethod(prev => (prev?.last4 ? prev : paymentInfo));
              const paymentIntentsRef = collection(db, 'paymentIntents');
              const paymentQuery = query(paymentIntentsRef, where('userId', '==', userId));
              const paymentSnapshot = await getDocs(paymentQuery);
              if (!paymentSnapshot.empty) {
                const succeeded = paymentSnapshot.docs
                  .filter(d => d.data().status === 'succeeded')
                  .sort((a, b) => {
                    const aTime = a.data().createdAt?.toDate?.()?.getTime() ?? 0;
                    const bTime = b.data().createdAt?.toDate?.()?.getTime() ?? 0;
                    return bTime - aTime;
                  });
                const recentPayment = succeeded[0];
                if (recentPayment?.id) loadPaymentMethodFromStripe(recentPayment.id);
              }
            }
          } else {
            // User document doesn't have valid subscription data
            setSubscriptionData(null);
          }
        } else {
          setSubscriptionData(null);
        }
      }
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError(t('subscription.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethodFromStripe = async (paymentIntentId, setResult = null) => {
    const setter = setResult || setPaymentMethod;
    try {
      setLoadingPaymentMethod(true);
      const functions = getFunctions();
      const getPaymentMethodFn = httpsCallable(functions, 'getPaymentMethodFromIntent');
      
      const result = await getPaymentMethodFn({ paymentIntentId });
      
      if (result.data && result.data.success && result.data.paymentMethod) {
        const pm = result.data.paymentMethod;
        const data = {
          last4: pm.last4,
          brand: pm.brand,
          expMonth: pm.exp_month,
          expYear: pm.exp_year,
          amount: result.data.amount,
          currency: result.data.currency || 'eur',
        };
        setter(data);
        return data;
      }
      return null;
    } catch (err) {
      console.error('Error loading payment method from Stripe:', err);
      return null;
    } finally {
      setLoadingPaymentMethod(false);
    }
  };

  const loadPurchaseHistory = async () => {
    try {
      if (!auth.currentUser) return;

      const userId = auth.currentUser.uid;

      // Only show real subscriptions (created when payment actually succeeded or admin-assigned).
      // Do NOT add standalone payment intents - those include checkout attempts that were never completed.
      const subscriptionsRef = collection(db, 'subscriptions');
      const q = query(subscriptionsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);

      const history = [];

      for (const docSnap of querySnapshot.docs) {
        const subData = docSnap.data();

        // Only show subscriptions that are:
        // 1. Admin-assigned (isAdminAssigned = true), OR
        // 2. Have a paymentIntentId with status 'succeeded' (real completed payment)
        let isValidSubscription = false;
        let paymentDetails = null;

        // Check if admin-assigned
        if (subData.isAdminAssigned === true) {
          isValidSubscription = true;
        }
        // Check if has valid payment intent
        else if (subData.paymentIntentId) {
          try {
            const paymentIntentRef = doc(db, 'paymentIntents', subData.paymentIntentId);
            const paymentIntentDoc = await getDoc(paymentIntentRef);
            if (paymentIntentDoc.exists()) {
              const paymentData = paymentIntentDoc.data();
              // Only include if payment actually succeeded
              if (paymentData.status === 'succeeded') {
                isValidSubscription = true;
                paymentDetails = {
                  amount: paymentData.amount ?? subData.amount,
                  currency: paymentData.currency || subData.currency || 'eur',
                  discountApplied: paymentData.discountApplied,
                };
              }
            }
          } catch (err) {
            console.error('Error fetching payment intent:', err);
          }
        }

        // Skip subscriptions that don't meet our criteria (incomplete checkouts, etc.)
        if (!isValidSubscription) {
          continue;
        }

        // In purchase history: show user-friendly status based on expiration date, not just Firestore status
        const now = new Date();
        const endDate = subData.endDate?.toDate();
        const cancelledAt = subData.cancelledAt?.toDate();
        const firestoreStatus = subData.status;

        let displayStatus = firestoreStatus;

        // Check if this is the currently active subscription (the one showing in the main card)
        const isCurrentActiveSubscription = subscriptionData && subscriptionData.id === docSnap.id;

        // Determine display status based on expiration and cancellation
        // Priority: Current active subscription > Expiration date > Cancellation status
        if (isCurrentActiveSubscription && endDate && now <= endDate) {
          // This is the current active subscription - always show as active
          displayStatus = 'active';
        } else if (cancelledAt) {
          // Subscription was cancelled
          // If cancelled but not expired yet, still show as cancelled (user cancelled but period not over)
          displayStatus = 'cancelled';
        } else if (endDate && now <= endDate) {
          // Subscription hasn't expired yet - show as active
          displayStatus = 'active';
        } else if (endDate && now > endDate) {
          // Subscription has expired - show as completed
          displayStatus = 'completed';
        } else {
          // No end date or other status - use Firestore status
          displayStatus = firestoreStatus || 'completed';
        }

        history.push({
          id: docSnap.id,
          planId: subData.planId,
          planName: subData.planName || (subData.planId === 'weekly' ? 'Weekly Plan' : subData.planId === 'monthly' ? 'Monthly Plan' : subData.planId === 'quarterly' ? '3 Months' : 'Premium'),
          status: displayStatus, // Use calculated status instead of raw Firestore status
          startDate: subData.startDate?.toDate(),
          endDate: endDate,
          createdAt: subData.createdAt?.toDate(),
          cancelledAt: cancelledAt,
          amount: paymentDetails?.amount ?? subData.amount,
          currency: paymentDetails?.currency || subData.currency || 'eur',
          paymentIntentId: subData.paymentIntentId,
          isAdminAssigned: subData.isAdminAssigned || false,
          discountApplied: paymentDetails?.discountApplied ?? subData.discountApplied,
        });
      }

      // Sort by date (most recent first)
      history.sort((a, b) => {
        const dateA = a.createdAt || a.startDate || new Date(0);
        const dateB = b.createdAt || b.startDate || new Date(0);
        return dateB - dateA;
      });

      setPurchaseHistory(history);
    } catch (err) {
      console.error('Error loading purchase history:', err);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsCanceling(true);
      setError(null);

      if (!auth.currentUser) {
        navigate('/auth');
        return;
      }

      const userId = auth.currentUser.uid;

      // Update subscription status in Firestore
      if (subscriptionData?.id) {
        const subscriptionRef = doc(db, 'subscriptions', subscriptionData.id);
        await updateDoc(subscriptionRef, {
          status: 'cancelled',
          cancelledAt: new Date(),
        });
      }

      // Update user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        subscriptionStatus: 'cancelled',
        lastUpdated: new Date(),
      });

      // Reload subscription data
      await loadSubscriptionData();
      setShowCancelConfirm(false);
      setHoldProgress(0);
      
      // Show success message
      alert(t('subscription.cancel.success'));
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(t('subscription.cancel.error'));
    } finally {
      setIsCanceling(false);
      setIsHolding(false);
      setHoldProgress(0);
    }
  };

  // Handle hold to cancel
  const handleHoldStart = () => {
    if (isCanceling) return;
    
    setIsHolding(true);
    setHoldProgress(0);
    
    const HOLD_DURATION = 2000; // 2 seconds
    const UPDATE_INTERVAL = 50; // Update every 50ms for smooth animation
    const progressIncrement = (UPDATE_INTERVAL / HOLD_DURATION) * 100;
    
    holdIntervalRef.current = setInterval(() => {
      setHoldProgress(prev => {
        const newProgress = prev + progressIncrement;
        if (newProgress >= 100) {
          clearInterval(holdIntervalRef.current);
          handleCancelSubscription();
          return 100;
        }
        return newProgress;
      });
    }, UPDATE_INTERVAL);
  };

  const handleHoldEnd = () => {
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
    setIsHolding(false);
    setHoldProgress(0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (holdIntervalRef.current) {
        clearInterval(holdIntervalRef.current);
      }
    };
  }, []);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCardNumber = (last4, brand) => {
    if (!last4) return 'N/A';
    const brandName = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';
    return `${brandName} •••• ${last4}`;
  };

  const formatAmount = (amount, currency = 'eur') => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: t('subscription.status.active') },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: t('subscription.status.cancelled') },
      expired: { bg: 'bg-gray-100', text: 'text-gray-800', label: t('subscription.status.expired') },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: t('subscription.status.completed') },
    };
    const config = statusConfig[status] || statusConfig.completed;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-[10px] sm:text-xs font-semibold shrink-0 ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-space">
        <div className="text-center">
          <LogamiLogoSpinner size="lg" className="mx-auto mb-4" decorative={false} alt="Logami loading" />
          <p className="text-gray-600">{t('subscription.loading')}</p>
        </div>
      </div>
    );
  }

  if (!subscriptionData) {
    return (
      <div className="min-h-screen bg-white font-space relative overflow-hidden">
        <GeometricBackground />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
          {/* Back button, desktop only; mobile uses bottom nav */}
          <div className="hidden sm:flex mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-semibold"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('subscription.back')}</span>
            </button>
          </div>

          <div className="mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 mb-1 sm:mb-2 tracking-tight">{t('subscription.title')}</h1>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">{t('subscription.subtitle')}</p>
          </div>

          {/* Debug Information - Show if there's an error or sync issue */}
          {error && (
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-bold text-yellow-900 mb-2">{t('subscription.syncIssue.title')}</h3>
                  <p className="text-yellow-800 mb-4">{error}</p>
                  <button
                    onClick={async () => {
                      try {
                        setError(null);
                        setLoading(true);
                        // Reload subscription data
                        await loadSubscriptionData();
                      } catch (err) {
                        console.error('Error reloading:', err);
                      }
                    }}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-yellow-700 transition-colors"
                  >
                    {t('subscription.syncIssue.refresh')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Free Account Status Card */}
          {subscriptionStatus && (
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-4 sm:p-6 md:p-8 mb-6 shadow-xl border border-gray-300">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/80 rounded-xl sm:rounded-2xl flex items-center justify-center border border-gray-300 shrink-0">
                    <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight text-gray-900 truncate">{t('subscription.free.title')}</h2>
                    <p className="text-sm sm:text-base text-gray-600 font-medium">{t('subscription.free.currentStatus')}</p>
                    {subscriptionStatus.status && (
                      <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                        Status: {subscriptionStatus.status} | Plan: {subscriptionStatus.plan}
                        {subscriptionStatus.endDate && ` | Expires: ${subscriptionStatus.endDate.toLocaleDateString()}`}
                      </p>
                    )}
                  </div>
                </div>
                <div className="bg-white/80 rounded-lg sm:rounded-xl px-2.5 py-1 sm:px-4 sm:py-2 border border-gray-300 shrink-0">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-gray-700">{t('subscription.free.status')}</span>
                </div>
              </div>
              
              <div className="bg-white/60 rounded-xl p-4 sm:p-6 mt-4 border border-gray-300">
                <h3 className="font-bold text-gray-900 mb-3 text-base sm:text-lg">{t('subscription.free.features.title')}</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    <span className="font-medium">{t('subscription.free.feature1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    <span className="font-medium">{t('subscription.free.feature2')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    <span className="font-medium">{t('subscription.free.feature3')}</span>
                  </li>
                </ul>
              </div>

              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-300">
                <h3 className="font-bold text-gray-900 mb-3 text-base sm:text-lg">{t('subscription.free.upgrade.title')}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 font-medium">
                  {t('subscription.free.upgrade.description')}
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="bg-black text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-bold hover:bg-[#3A86FF] transition-all duration-300 border-2 border-black hover:border-[#3A86FF] text-base sm:text-lg min-h-[48px]"
                >
                  {t('subscription.free.upgrade.button')}
                </button>
              </div>
            </div>
          )}

          {/* Fallback if subscriptionStatus is not loaded yet */}
          {!subscriptionStatus && (
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200 border border-gray-200 p-4 sm:p-8 text-center mb-6">
              <CreditCard className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{t('subscription.noActive.title')}</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-6">{t('subscription.noActive.description')}</p>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-black text-white px-6 py-3 rounded-xl font-bold hover:bg-[#3A86FF] transition-all duration-300 border-2 border-black hover:border-[#3A86FF]"
              >
                {t('subscription.noActive.viewPlans')}
              </button>
            </div>
          )}

          {/* Last payment method - when no active subscription but user has a past purchase with payment intent */}
          {(() => {
            const lastPurchaseWithPayment = purchaseHistory.find(p => p.paymentIntentId);
            return lastPurchaseWithPayment && (
              <div className="bg-white rounded-2xl shadow-xl shadow-gray-200 border border-gray-200 p-4 sm:p-6 mb-6">
                <div className="flex items-center justify-between mb-4 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#3A86FF]/10 rounded-xl flex items-center justify-center shrink-0">
                      <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A86FF]" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-base sm:text-lg">{t('subscription.paymentMethod.title')}</h3>
                  </div>
                  {(!lastPaymentMethod || !lastPaymentMethod.last4) && !loadingPaymentMethod && (
                    <button
                      onClick={() => loadPaymentMethodFromStripe(lastPurchaseWithPayment.paymentIntentId, setLastPaymentMethod)}
                      className="px-4 py-2 rounded-lg bg-[#3A86FF] text-white text-sm font-semibold hover:bg-blue-700"
                    >
                      {t('subscription.paymentMethod.loadFromStripe')}
                    </button>
                  )}
                </div>
                {loadingPaymentMethod ? (
                  <div className="flex items-center gap-2 text-gray-600">
                    <LogamiLogoSpinner size="sm" decorative={false} alt="Logami loading" />
                    <p className="font-medium">{t('subscription.paymentMethod.loading')}</p>
                  </div>
                ) : lastPaymentMethod?.last4 ? (
                  <div>
                    <p className="text-base sm:text-lg md:text-xl font-black text-gray-900 tracking-tight">
                      {formatCardNumber(lastPaymentMethod.last4, lastPaymentMethod.brand)}
                    </p>
                    {lastPaymentMethod.expMonth && lastPaymentMethod.expYear && (
                      <p className="text-sm text-gray-500 mt-1 font-semibold">
                        {t('subscription.paymentMethod.expires')}: {String(lastPaymentMethod.expMonth).padStart(2, '0')}/{String(lastPaymentMethod.expYear).slice(-2)}
                      </p>
                    )}
                    {lastPaymentMethod.amount != null && (
                      <p className="text-sm text-gray-500 mt-2 font-semibold">
                        {t('subscription.paymentMethod.amount')}: {formatAmount(lastPaymentMethod.amount, lastPaymentMethod.currency || 'eur')}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-600 font-medium">
                    {t('subscription.paymentMethod.stripeDetails')}
                  </p>
                )}
              </div>
            );
          })()}

          {/* Purchase History - Show even if no active subscription */}
          {purchaseHistory.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200 border-2 border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#3A86FF]/10 rounded-xl flex items-center justify-center">
                  <History className="w-6 h-6 text-[#3A86FF]" />
                </div>
                <h3 className="font-bold text-gray-900 text-lg">{t('subscription.purchaseHistory.title')}</h3>
              </div>
              <div className="space-y-4">
                {purchaseHistory.map((purchase) => (
                  <div
                    key={purchase.id}
                    className="border-2 border-gray-200 rounded-xl p-4 hover:border-[#3A86FF] transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-3 mb-2">
                          <h4 className="font-bold text-gray-900 text-sm sm:text-base">{purchase.planName}</h4>
                          {getStatusBadge(purchase.status)}
                          {purchase.isAdminAssigned && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] sm:text-xs font-semibold bg-purple-100 text-purple-800 shrink-0">
                              {t('subscription.purchaseHistory.adminAssigned')}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div>
                            <span className="font-semibold">{t('subscription.purchaseHistory.purchaseDate')}:</span>{' '}
                            {formatDate(purchase.createdAt || purchase.startDate)}
                          </div>
                          {purchase.endDate && (
                            <div>
                              <span className="font-semibold">{t('subscription.purchaseHistory.expires')}:</span>{' '}
                              {formatDate(purchase.endDate)}
                            </div>
                          )}
                          {purchase.amount !== undefined && (
                            <div>
                              <span className="font-semibold">{t('subscription.purchaseHistory.amount')}:</span>{' '}
                              {formatAmount(purchase.amount, purchase.currency)}
                            </div>
                          )}
                          {purchase.discountApplied && (
                            <div className="text-green-600">
                              <span className="font-semibold">{t('subscription.purchaseHistory.discountApplied')}:</span>{' '}
                              {purchase.discountApplied.type === 'percentage'
                                ? `${purchase.discountApplied.value}%`
                                : formatAmount(purchase.discountApplied.value, purchase.currency)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <GrammarBottomNav
          buttons={[
            { id: 'back', icon: ArrowLeft, label: t('nav.back', { defaultValue: 'Back' }), onClick: () => (window.history.length > 1 ? navigate(-1) : navigate('/profile')), color: TEAL },
            { id: 'home', icon: Home, label: t('nav.home', { defaultValue: 'Home' }), onClick: () => navigate('/dashboard'), color: TEAL },
            { id: 'profile', icon: User, label: t('nav.profile', { defaultValue: 'Profile' }), onClick: () => navigate('/profile'), color: TEAL },
          ]}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-space relative overflow-hidden">
      <GeometricBackground />
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        {/* Back button, desktop only; mobile uses bottom nav */}
        <div className="hidden sm:flex mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-semibold"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
        </div>

        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 mb-1 sm:mb-2 tracking-tight">Subscription Management</h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600">Manage your subscription and payment details</p>
        </div>

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 flex items-center gap-3 shadow-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700 font-semibold">{error}</p>
          </div>
        )}

        {/* Subscription Status Card */}
        <div className="bg-gradient-to-br from-[#3A86FF] to-blue-600 rounded-2xl p-4 sm:p-6 md:p-8 text-white mb-6 shadow-xl border border-[#3A86FF]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0">
                <Crown className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight truncate">{t('subscription.premium.title')}</h2>
                <p className="text-sm sm:text-base text-blue-100 font-medium truncate">{subscriptionData.planName || subscriptionData.planId}</p>
              </div>
            </div>
            <div className="bg-white/20 rounded-lg sm:rounded-xl px-2.5 py-1 sm:px-4 sm:py-2 border border-white/30 shrink-0">
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider">{t('subscription.premium.active')}</span>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
          {/* Purchase Date */}
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200 border border-gray-200 p-4 sm:p-6 transition-all duration-300 hover:border-[#3A86FF] hover:shadow-2xl">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#3A86FF]/10 rounded-xl flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A86FF]" />
              </div>
              <h3 className="font-bold text-gray-900 text-base sm:text-lg">{t('subscription.purchaseDate')}</h3>
            </div>
            <p className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
              {formatDate(subscriptionData.startDate || subscriptionData.createdAt)}
            </p>
          </div>

          {/* Expiration Date */}
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200 border border-gray-200 p-4 sm:p-6 transition-all duration-300 hover:border-[#3A86FF] hover:shadow-2xl">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#3A86FF]/10 rounded-xl flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A86FF]" />
              </div>
              <h3 className="font-bold text-gray-900 text-base sm:text-lg">{t('subscription.expiresOn')}</h3>
            </div>
            <p className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
              {formatDate(subscriptionData.endDate)}
            </p>
            {subscriptionData.endDate && (
              <p className="text-sm text-gray-500 mt-2 font-semibold">
                {t('subscription.daysRemaining', { count: Math.max(0, Math.floor((subscriptionData.endDate - new Date()) / (1000 * 60 * 60 * 24))) })}
              </p>
            )}
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200 border border-gray-200 p-4 sm:p-6 mb-6 transition-all duration-300 hover:border-[#3A86FF] hover:shadow-2xl">
          <div className="flex items-center justify-between mb-4 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#3A86FF]/10 rounded-xl flex items-center justify-center shrink-0">
                <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A86FF]" />
              </div>
              <h3 className="font-bold text-gray-900 text-base sm:text-lg">{t('subscription.paymentMethod.title')}</h3>
            </div>
            {subscriptionData?.paymentIntentId && (!paymentMethod || !paymentMethod.last4) && !loadingPaymentMethod && (
              <button
                onClick={() => loadPaymentMethodFromStripe(subscriptionData.paymentIntentId)}
                className="text-sm text-[#3A86FF] hover:text-blue-700 font-semibold"
              >
                Load from Stripe
              </button>
            )}
          </div>
          {loadingPaymentMethod ? (
            <div className="flex items-center gap-2 text-gray-600">
              <LogamiLogoSpinner size="sm" decorative={false} alt="Logami loading" />
              <p className="font-medium">Loading payment method...</p>
            </div>
          ) : paymentMethod && paymentMethod.last4 ? (
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-base sm:text-lg md:text-xl font-black text-gray-900 tracking-tight">
                  {formatCardNumber(paymentMethod.last4, paymentMethod.brand)}
                </p>
                {paymentMethod.expMonth && paymentMethod.expYear && (
                  <p className="text-sm text-gray-500 mt-1 font-semibold">
                    {t('subscription.paymentMethod.expires')}: {String(paymentMethod.expMonth).padStart(2, '0')}/{String(paymentMethod.expYear).slice(-2)}
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-2 font-semibold">
                  {t('subscription.paymentMethod.amount')}: {formatAmount(paymentMethod.amount || subscriptionData.amount || 0, paymentMethod.currency || 'eur')}
                </p>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-600 font-medium mb-2">{t('subscription.paymentMethod.notAvailable')}</p>
              {subscriptionData?.paymentIntentId && (
                <p className="text-xs text-gray-500">
                  {t('subscription.paymentMethod.stripeInfo')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Purchase History */}
        {purchaseHistory.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200 border border-gray-200 p-4 sm:p-6 mb-6">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#3A86FF]/10 rounded-xl flex items-center justify-center shrink-0">
                <History className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A86FF]" />
              </div>
              <h3 className="font-bold text-gray-900 text-base sm:text-lg">Purchase History</h3>
            </div>
            <div className="space-y-4">
              {purchaseHistory.map((purchase) => (
                <div
                  key={purchase.id}
                  className="border border-gray-200 rounded-xl p-3 sm:p-4 hover:border-[#3A86FF] transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-3 mb-2">
                        <h4 className="font-bold text-gray-900 text-sm sm:text-base">{purchase.planName}</h4>
                        {getStatusBadge(purchase.status)}
                        {purchase.isAdminAssigned && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] sm:text-xs font-semibold bg-purple-100 text-purple-800 shrink-0">
                            Admin Assigned
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>
                          <span className="font-semibold">Purchase Date:</span>{' '}
                          {formatDate(purchase.createdAt || purchase.startDate)}
                        </div>
                        {purchase.endDate && (
                          <div>
                            <span className="font-semibold">Expires:</span>{' '}
                            {formatDate(purchase.endDate)}
                          </div>
                        )}
                        {purchase.amount !== undefined && (
                          <div>
                            <span className="font-semibold">Amount:</span>{' '}
                            {formatAmount(purchase.amount, purchase.currency)}
                          </div>
                        )}
                        {purchase.discountApplied && (
                          <div className="text-green-600">
                            <span className="font-semibold">Discount Applied:</span>{' '}
                            {purchase.discountApplied.type === 'percentage'
                              ? `${purchase.discountApplied.value}%`
                              : formatAmount(purchase.discountApplied.value, purchase.currency)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancel Subscription */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200 border border-red-200 p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2 text-lg">{t('subscription.cancel.title')}</h3>
              <p className="text-sm text-gray-600 mb-6 font-medium">
                {t('subscription.cancel.description', { date: formatDate(subscriptionData.endDate) })}
              </p>
              {!showCancelConfirm ? (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="bg-black text-white px-6 py-3 rounded-xl font-bold hover:bg-red-600 transition-all duration-300 border-2 border-black hover:border-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('subscription.cancel.button')}
                </button>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm font-bold text-gray-900 mb-2">{t('subscription.cancel.confirm')}</p>
                  <div className="relative">
                    <button
                      onMouseDown={handleHoldStart}
                      onMouseUp={handleHoldEnd}
                      onMouseLeave={handleHoldEnd}
                      onTouchStart={handleHoldStart}
                      onTouchEnd={handleHoldEnd}
                      disabled={isCanceling}
                      className="relative w-full bg-red-600 text-white px-6 py-4 rounded-xl font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border-2 border-red-600 hover:border-red-700 overflow-hidden touch-manipulation"
                    >
                      {/* Progress bar fill */}
                      <div
                        className="absolute inset-0 bg-red-700 transition-all duration-75 ease-linear"
                        style={{
                          width: `${holdProgress}%`,
                          left: 0,
                          top: 0,
                          bottom: 0,
                        }}
                      />
                      
                      {/* Button content */}
                      <span className="relative z-10 flex items-center gap-2">
                        {isCanceling ? (
                          <>
                            <LogamiLogoSpinner size="md" decorative={false} alt="Logami loading" />
                            <span>{t('subscription.cancel.cancelling')}</span>
                          </>
                        ) : isHolding ? (
                          <>
                            <XCircle className="w-5 h-5" />
                            <span>{t('subscription.cancel.holdToCancelProgress', { progress: Math.round(holdProgress) })}</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5" />
                            <span>{t('subscription.cancel.holdToCancel')}</span>
                          </>
                        )}
                      </span>
                    </button>
                    
                    {/* Progress indicator text */}
                    {isHolding && holdProgress > 0 && holdProgress < 100 && (
                      <p className="text-xs text-gray-500 mt-2 text-center font-semibold">
                        {t('subscription.cancel.keepHolding', { progress: Math.round(holdProgress) })}
                      </p>
                    )}
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowCancelConfirm(false);
                      handleHoldEnd();
                    }}
                    disabled={isCanceling || isHolding}
                    className="w-full bg-white text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 disabled:opacity-50 border-2 border-gray-300 hover:border-gray-400"
                  >
                    {t('subscription.cancel.cancelButton')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <GrammarBottomNav
        buttons={[
          { id: 'back', icon: ArrowLeft, label: t('nav.back', { defaultValue: 'Back' }), onClick: () => (window.history.length > 1 ? navigate(-1) : navigate('/profile')), color: TEAL },
          { id: 'home', icon: Home, label: t('nav.home', { defaultValue: 'Home' }), onClick: () => navigate('/dashboard'), color: TEAL },
          { id: 'profile', icon: User, label: t('nav.profile', { defaultValue: 'Profile' }), onClick: () => navigate('/profile'), color: TEAL },
        ]}
      />
    </div>
  );
};

export default SubscriptionManagementPage;
